/**
 * ReceivedGrid — the "Received" sub-tab of the v2 Fax workspace.
 *
 * Per PART 7 of the /phone rebuild spec:
 *   - 2-across card grid
 *   - Each card: sender name/number, time, page count
 *   - AI matcher result block per PART 7 buckets:
 *       confident (≥80%)      → green "Assign to chart" + "Change"
 *       possible / uncertain   → navy "Pick a chart" + "Junk"
 *       already filed          → 72% opacity, "Open chart" + "Reassign"
 *   - Top border: gold=needs filing, amber=unknown sender, none=filed
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, Loader2, Ban, Eye } from "lucide-react";
import { faxAPI } from "./faxAPI";
import ChartPickerModal from "./ChartPickerModal";
import { formatPhoneNumber } from "../../../lib/utils";
import { NAVY, GOLD, GREEN, AMBER, NAVY_HAIRLINE, NAVY_TINT } from "../tokens";
import ViewToggle, { useViewMode } from "../ViewToggle";

const fmtDate = (iso) => !iso ? "" : new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

function bucketOf(fax) {
  const filed = !!fax.assigned_patient_id || !!fax.filed_to || fax.status === "filed";
  const conf = Number(fax.match_confidence || fax.ai_confidence || 0);
  if (filed) return "filed";
  if (conf >= 0.8) return "confident";
  if (conf >= 0.3) return "possible";
  return "unknown";
}

function ReceivedCard({ fax, onPick, onAssign, onDismiss, onOpenChart, busyId }) {
  const b = bucketOf(fax);
  const pct = Math.round((fax.match_confidence || fax.ai_confidence || 0) * 100);
  const opacity = b === "filed" ? 0.72 : 1;
  const topBorder =
      b === "filed"   ? "transparent"
    : b === "unknown" ? AMBER
    :                   GOLD;
  const isBusy = busyId === fax.id;

  return (
    <div
      className="rounded-xl bg-white border p-3"
      style={{ borderColor: NAVY_HAIRLINE, borderTop: `3px solid ${topBorder}`, opacity }}
      data-testid={`comms2-fax-received-${fax.id}`}
    >
      <div className="flex items-baseline gap-2">
        <div className="text-[13px] font-semibold truncate flex-1" style={{ color: NAVY }}>
          {fax.from_name || fax.sender_name || formatPhoneNumber(fax.from_number || fax.sender_number) || "Unknown sender"}
        </div>
        <div className="text-[10px] text-slate-400 tabular-nums shrink-0">{fmtDate(fax.received_at || fax.created_at)}</div>
      </div>
      <div className="text-[11.5px] text-slate-600 mt-0.5">
        {(fax.page_count || fax.pages || "?") + " pages"}
        {fax.subject ? ` · ${fax.subject}` : ""}
      </div>

      {/* Matcher block */}
      <div className="mt-2 rounded-md p-2 text-[11.5px]" style={{ background: NAVY_TINT, color: NAVY }}>
        {b === "filed" && (
          <>Filed to <span className="font-semibold">{fax.filed_to || fax.assigned_patient_name || fax.assigned_patient_id}</span>
          {fax.filed_by ? ` by ${fax.filed_by}` : ""}
          {fax.filed_at ? ` on ${fmtDate(fax.filed_at)}` : ""}</>
        )}
        {b === "confident" && (
          <>Likely: <span className="font-semibold">{fax.suggested_patient_name || fax.ai_top_candidate_name}</span>
          {" · "}<span className="font-semibold">{fax.suggested_doc_type || "unknown doc"}</span>
          {" · "}<span className="font-semibold">{pct}% match</span></>
        )}
        {b === "possible" && (
          <>Possible: <span className="font-semibold">{fax.suggested_patient_name || fax.ai_top_candidate_name || "?"}</span>
          {" · "}<span className="font-semibold">{pct}%</span> · {fax.candidate_count || "several"} candidates</>
        )}
        {b === "unknown" && (
          <>No confident match · {fax.candidate_count || "?"} possible clients</>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t" style={{ borderColor: NAVY_HAIRLINE }}>
        {b === "filed" && (
          <>
            <button
              onClick={() => onOpenChart(fax)}
              className="h-8 px-2.5 rounded-md border text-[11.5px] font-medium flex items-center gap-1"
              style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
              data-testid={`comms2-fax-open-${fax.id}`}
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open chart
            </button>
            <button
              onClick={() => onPick(fax)}
              className="h-8 px-2.5 rounded-md border text-[11.5px] font-medium hover:bg-slate-50"
              style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
              data-testid={`comms2-fax-reassign-${fax.id}`}
            >
              Reassign
            </button>
          </>
        )}
        {b === "confident" && (
          <>
            <button
              onClick={() => onAssign(fax)}
              disabled={isBusy}
              className="h-8 px-3 rounded-md text-white text-[11.5px] font-semibold flex items-center gap-1 disabled:opacity-40"
              style={{ background: GREEN }}
              data-testid={`comms2-fax-assign-${fax.id}`}
            >
              {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Assign to chart
            </button>
            <button
              onClick={() => onPick(fax)}
              className="h-8 px-2.5 rounded-md border text-[11.5px] font-medium hover:bg-slate-50"
              style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
              data-testid={`comms2-fax-change-${fax.id}`}
            >
              Change
            </button>
          </>
        )}
        {(b === "possible" || b === "unknown") && (
          <>
            <button
              onClick={() => onPick(fax)}
              className="h-8 px-3 rounded-md text-white text-[11.5px] font-semibold"
              style={{ background: NAVY }}
              data-testid={`comms2-fax-pick-${fax.id}`}
            >
              Pick a chart
            </button>
            <button
              onClick={() => onDismiss(fax)}
              className="h-8 px-2.5 rounded-md border text-[11.5px] font-medium hover:bg-slate-50 flex items-center gap-1"
              style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
              data-testid={`comms2-fax-junk-${fax.id}`}
              title="Mark as junk (not filed to any chart)"
            >
              <Ban className="w-3.5 h-3.5" /> Junk
            </button>
          </>
        )}
        <a
          href={faxAPI.downloadIncoming(fax.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto h-8 w-8 rounded-md border flex items-center justify-center hover:bg-slate-50"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
          title="Preview"
          data-testid={`comms2-fax-preview-${fax.id}`}
        >
          <Eye className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}

/* ── Compact row for list view (iter 489) ── */
function ReceivedRow({ fax, onPick, onAssign, onDismiss, onOpenChart, busyId, isLast }) {
  const b = bucketOf(fax);
  const pct = Math.round((fax.match_confidence || fax.ai_confidence || 0) * 100);
  const opacity = b === "filed" ? 0.72 : 1;
  const topBorder = b === "filed" ? "transparent" : b === "unknown" ? AMBER : GOLD;
  const isBusy = busyId === fax.id;
  return (
    <div
      className={`grid grid-cols-[3px_minmax(0,1.6fr)_minmax(0,1.4fr)_120px_180px] items-center gap-3 px-3 py-2 bg-white text-[12px] ${isLast ? "" : "border-b"}`}
      style={{ borderColor: NAVY_HAIRLINE, opacity }}
      data-testid={`comms2-fax-received-row-${fax.id}`}
    >
      <div className="h-6 rounded-sm" style={{ background: topBorder }} />
      <div className="min-w-0">
        <div className="font-semibold truncate" style={{ color: NAVY }}>
          {fax.from_name || fax.sender_name || formatPhoneNumber(fax.from_number || fax.sender_number) || "Unknown sender"}
        </div>
        <div className="text-[10.5px] text-slate-500 truncate">
          {(fax.page_count || fax.pages || "?") + " pages"}
          {fax.subject ? ` · ${fax.subject}` : ""}
        </div>
      </div>
      <div className="text-[11px] truncate" style={{ color: NAVY }}>
        {b === "filed"     && <>Filed to <span className="font-semibold">{fax.filed_to || fax.assigned_patient_name || fax.assigned_patient_id}</span></>}
        {b === "confident" && <>Likely: <span className="font-semibold">{fax.suggested_patient_name || fax.ai_top_candidate_name}</span> · {pct}%</>}
        {b === "possible"  && <>Possible: <span className="font-semibold">{fax.suggested_patient_name || fax.ai_top_candidate_name || "?"}</span> · {pct}%</>}
        {b === "unknown"   && <span className="text-slate-500">No confident match</span>}
      </div>
      <div className="text-[11px] text-slate-600 tabular-nums truncate">{fmtDate(fax.received_at || fax.created_at)}</div>
      <div className="flex items-center justify-end gap-1.5">
        {b === "filed" && (
          <button onClick={() => onOpenChart(fax)} className="h-7 px-2 rounded border text-[11px] font-medium hover:bg-slate-50" style={{ borderColor: NAVY_HAIRLINE, color: NAVY }} data-testid={`comms2-fax-row-open-${fax.id}`}>Open</button>
        )}
        {b === "confident" && (
          <button onClick={() => onAssign(fax)} disabled={isBusy} className="h-7 px-2 rounded text-white text-[11px] font-semibold flex items-center gap-1 disabled:opacity-40" style={{ background: GREEN }} data-testid={`comms2-fax-row-assign-${fax.id}`}>
            {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Assign
          </button>
        )}
        {(b === "possible" || b === "unknown") && (
          <>
            <button onClick={() => onPick(fax)} className="h-7 px-2 rounded text-white text-[11px] font-semibold" style={{ background: NAVY }} data-testid={`comms2-fax-row-pick-${fax.id}`}>Pick</button>
            <button onClick={() => onDismiss(fax)} className="h-7 px-2 rounded border text-[11px] font-medium hover:bg-slate-50 flex items-center gap-1" style={{ borderColor: NAVY_HAIRLINE, color: NAVY }} data-testid={`comms2-fax-row-junk-${fax.id}`}>
              <Ban className="w-3 h-3" /> Junk
            </button>
          </>
        )}
        <a href={faxAPI.downloadIncoming(fax.id)} target="_blank" rel="noopener noreferrer" className="h-7 w-7 rounded border flex items-center justify-center hover:bg-slate-50" style={{ borderColor: NAVY_HAIRLINE, color: NAVY }} title="Preview" data-testid={`comms2-fax-row-preview-${fax.id}`}>
          <Eye className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

export default function ReceivedGrid({ navigate, faxes, loading, gap, onRefresh }) {
  const [pickFax, setPickFax] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [view, setView] = useViewMode("comms2-fax-received-view", "cards");

  const assignConfident = async (fax) => {
    setBusyId(fax.id);
    try {
      // Use AI's own top candidate + suggested doc type. Any override
      // goes through the picker instead.
      await faxAPI.routeToIntake(fax.id, {
        patient_id: fax.suggested_patient_id || fax.ai_top_candidate_id,
        doc_type: fax.suggested_doc_type || "orders",
      });
      toast.success("Filed to chart");
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.message || "Assign failed");
    } finally { setBusyId(null); }
  };

  const dismiss = async (fax) => {
    if (!window.confirm("Mark this fax as junk? It stays in history but not filed.")) return;
    try {
      // The dismiss endpoint expects unmatched-record id, not humblefax id.
      // Fall back gracefully if not applicable.
      const unmatchedId = fax.unmatched_id;
      if (!unmatchedId) { toast.error("This fax has no unmatched-queue entry to dismiss"); return; }
      await faxAPI.dismissUnmatched(unmatchedId, "junk");
      toast.success("Marked as junk");
      onRefresh && onRefresh();
    } catch (e) { toast.error(e.message || "Dismiss failed"); }
  };

  const openChart = (fax) => {
    const pid = fax.assigned_patient_id || fax.filed_to_id;
    if (pid) navigate(`/healthcare/patients/${pid}`);
    else toast.error("This fax has no assigned chart");
  };

  return (
    <>
      <div className="p-4">
        {/* View toggle row (iter 489) */}
        {!loading && !gap && faxes.length > 0 && (
          <div className="flex items-center justify-end mb-3">
            <ViewToggle view={view} setView={setView} testid="comms2-fax-received" />
          </div>
        )}
        {loading && <div className="text-center text-[13px] text-slate-400 py-8"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading received faxes…</div>}
        {!loading && gap && <div className="text-center text-[13px] text-slate-500 italic py-8">{gap}</div>}
        {!loading && !gap && faxes.length === 0 && <div className="text-center text-[13px] text-slate-400 py-8">Inbox is clear — no received faxes.</div>}
        {faxes.length > 0 && view === "cards" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {faxes.map(f => (
              <ReceivedCard
                key={f.id}
                fax={f}
                onPick={setPickFax}
                onAssign={assignConfident}
                onDismiss={dismiss}
                onOpenChart={openChart}
                busyId={busyId}
              />
            ))}
          </div>
        )}
        {faxes.length > 0 && view === "list" && (
          <div className="rounded-lg border overflow-hidden bg-white" style={{ borderColor: NAVY_HAIRLINE }} data-testid="comms2-fax-received-list">
            <div className="grid grid-cols-[3px_minmax(0,1.6fr)_minmax(0,1.4fr)_120px_180px] text-[10.5px] uppercase font-semibold tracking-wider text-slate-500 px-3 py-1.5 border-b" style={{ borderColor: NAVY_HAIRLINE, background: NAVY_TINT }}>
              <span />
              <span>Sender</span>
              <span>Match</span>
              <span>Received</span>
              <span className="text-right">Actions</span>
            </div>
            {faxes.map((f, i, arr) => (
              <ReceivedRow
                key={f.id}
                fax={f}
                onPick={setPickFax}
                onAssign={assignConfident}
                onDismiss={dismiss}
                onOpenChart={openChart}
                busyId={busyId}
                isLast={i === arr.length - 1}
              />
            ))}
          </div>
        )}
      </div>
      {pickFax && (
        <ChartPickerModal
          fax={pickFax}
          onClose={() => setPickFax(null)}
          onFiled={() => { setPickFax(null); onRefresh && onRefresh(); }}
        />
      )}
    </>
  );
}
